import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Global side-effect: if the URL contains `#session_id=...`, exchange it via
 * the backend for a session_token, drop it into AuthContext, and clean the URL.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 * Mount this once, high in the app tree (App.js), so any route can be a valid
 * OAuth landing page.
 */
const GoogleAuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    const hash = location.hash || window.location.hash;
    if (!hash || !hash.includes('session_id=')) return;
    if (processed.current) return;
    processed.current = true;

    const params = new URLSearchParams(hash.replace(/^#/, ''));
    const sessionId = params.get('session_id');
    if (!sessionId) return;

    (async () => {
      try {
        const res = await axios.post(`${BACKEND_URL}/api/auth/google/session`, {
          session_id: sessionId,
        });
        if (res.data?.success) {
          setSession(res.data.session_token, res.data.user);
          toast.success(`Welcome, ${res.data.user?.name || 'traveler'}!`);
          // Strip the fragment and keep the current pathname
          const clean = window.location.pathname + window.location.search;
          window.history.replaceState({}, document.title, clean);
          // If they landed on /login, bounce to home
          if (window.location.pathname === '/login') {
            navigate('/', { replace: true });
          }
        } else {
          toast.error('Google sign-in did not complete. Please try again.');
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Google sign-in failed');
      }
    })();
  }, [location, navigate, setSession]);

  return null;
};

export default GoogleAuthCallback;
